import React, { FC, useEffect, useState } from "react"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Slider from "@mui/material/Slider"
import Typography from "@mui/material/Typography"
import SectionWrapper from "./LockedSectionWrapper"
import VideoPlayer from "../../player/VideoPlayer"
import {
  resolveFourView,
  resolvePaneLayout,
  DEFAULT_FOUR_VIEW,
  FOUR_VIEW_ASPECT_RANGE,
  FOUR_VIEW_ZOOM_RANGE,
  type FourViewFraming,
} from "../../player/splitLayout"
import { Participant, Video } from "../../types"

// How the player's Grid layout frames its people (splitLayout.ts): the
// panels' shape and how far each zooms in. Gavin, 21 Oct, asked for narrower
// panels and bigger people, and for it to be something set here. Framing is
// presentation, outside the manifest, so it stays editable once sealed.
//
// All views is one panel per participant (22 Oct: no more fixed three and
// four), so the one setting frames two, three or four panels — however many
// people this video has. It is still stored as `four_view`, from when only
// four panels had it.
//
// The preview below opens in All views, halfway through (past the intro card),
// with only play, skip and the scrub bar; it follows the sliders as they move,
// and letting go of a slider saves.

interface FourViewSectionProps {
  video: Video
  participants?: Participant[]
  rotationAdjustment?: number
  onSave: (framing: FourViewFraming | null) => void
}

const ASPECT_MARKS = [
  { value: 1, label: "1:1" },
  { value: 5 / 4, label: "5:4" },
  { value: 4 / 3, label: "4:3" },
  { value: 16 / 9, label: "16:9" },
]

const ZOOM_MARKS = [1, 1.5, 2, 2.5, 3].map((value) => ({ value, label: `${value}×` }))

const FourViewSection: FC<FourViewSectionProps> = ({ video, participants, rotationAdjustment, onSave }) => {
  const stored = resolveFourView(video.four_view)
  const [framing, setFraming] = useState<FourViewFraming>(stored)

  // Follow the server when the stored value changes under us (a save landing,
  // another tab).
  useEffect(() => {
    setFraming(resolveFourView(video.four_view))
  }, [video.four_view?.pane_aspect, video.four_view?.zoom])

  const isDefault = !video.four_view
  const people = participants?.length ?? 0
  const panels = resolvePaneLayout('all', people).count

  return (
    <SectionWrapper
      title="All-views framing"
      status="pending"
      actions={
        <Button
          size="small"
          variant="outlined"
          disabled={isDefault}
          onClick={() => {
            setFraming(DEFAULT_FOUR_VIEW)
            onSave(null)
          }}
        >
          Reset to default
        </Button>
      }
    >
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        How the player's Grid layout frames each person: one panel per participant
        {people >= 2 ? ` — ${panels} panels for this video's ${people} people${people > panels ? " (the first four)" : ""}` : ""}.
        Applies to every viewer; can be changed after sealing.
      </Typography>
      {people < 2 && (
        <Typography variant="body2" sx={{ mb: 2, color: "warning.main" }}>
          This video has {people === 0 ? "no participants" : "one participant"}, so viewers are not offered Grid. Add participants to use it.
        </Typography>
      )}

      <Box sx={{ px: 1 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>Panel shape (width : height)</Typography>
        <Slider
          value={framing.paneAspect}
          min={FOUR_VIEW_ASPECT_RANGE.min}
          max={FOUR_VIEW_ASPECT_RANGE.max}
          step={0.01}
          marks={ASPECT_MARKS}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v.toFixed(2)} : 1`}
          onChange={(_, v) => setFraming((f) => ({ ...f, paneAspect: v as number }))}
          onChangeCommitted={(_, v) => onSave({ ...framing, paneAspect: v as number })}
        />

        <Typography variant="caption" sx={{ color: "text.secondary" }}>Subject size (zoom)</Typography>
        <Slider
          value={framing.zoom}
          min={FOUR_VIEW_ZOOM_RANGE.min}
          max={FOUR_VIEW_ZOOM_RANGE.max}
          step={0.05}
          marks={ZOOM_MARKS}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v.toFixed(2)}×`}
          onChange={(_, v) => setFraming((f) => ({ ...f, zoom: v as number }))}
          onChangeCommitted={(_, v) => onSave({ ...framing, zoom: v as number })}
        />
      </Box>

      {video.stream_url && (
        <Box sx={{ mt: 1 }}>
          <VideoPlayer
            src={video.stream_url}
            type="application/x-mpegURL"
            participants={participants || []}
            rotationAdjustment={rotationAdjustment}
            fourView={{ pane_aspect: framing.paneAspect, zoom: framing.zoom }}
            initialSplitLayout="all"
            initialTimeFraction={0.5}
            preview
            compact
          />
        </Box>
      )}
    </SectionWrapper>
  )
}

export default FourViewSection
